/**
 * Fleet bubble: a floating arrow pointing at the nearest other driver with the
 * distance to them. Tap it to open the roster, pick a driver, and send one of
 * three fixed questions. Their answer comes back as a toast.
 *
 * Everything the driver reads or hears is rendered from an enum on this device,
 * in this device's language and voice - see fleet/fleet.ts for why the wire
 * format carries 'food' | 'fuel' | 'help' rather than a sentence.
 *
 * Designed for glances, not reading: the arrow rotates to point at the other
 * car so its direction is understood without parsing text, targets are large,
 * and the incoming prompt is a two-button yes/no that is also spoken aloud.
 */

import type { FleetContact, MessageKind, MessageReply, QuickMessage, SharedDest } from '../fleet/fleet';
import { formatSpeed, speedUnitLabel } from '../core/format';
import type { UnitSystem } from '../core/settings';
import { t } from '../core/i18n';

/* The three askable questions. 'dest' is deliberately not here - sharing a
   destination has its own button because it carries a payload. */
const KINDS: Array<{ kind: MessageKind; icon: string }> = [
  { kind: 'food', icon: '🍜' },
  { kind: 'fuel', icon: '⛽' },
  { kind: 'help', icon: '🆘' },
];

const ICONS: Record<MessageKind, string> = { food: '🍜', fuel: '⛽', help: '🆘', dest: '📍' };

export interface FleetBubbleActions {
  onSend: (to: number, kind: MessageKind) => void;
  onReply: (id: string, reply: MessageReply) => void;
  /**
   * The destination this driver could share right now (preview or navigating),
   * or null. Read at render time so the sheet always reflects the live trip.
   */
  shareableDest: () => SharedDest | null;
  /** This driver's live fix, ready to share as a navigable point. */
  shareableLocation: () => SharedDest | null;
  /** Send my current destination to one driver. */
  onShareDest: (to: number) => void;
  /** Send my current GPS position to one driver. */
  onShareLocation: (to: number) => void;
  /** The driver accepted a shared destination - navigate there. */
  onGotoDest: (dest: SharedDest) => void;
}

/**
 * How many drivers get their own bubble before the rest collapse into a "+N".
 * A convoy of three is the common case and all three deserve to be seen at a
 * glance; beyond that the column would start eating the map it is drawn on.
 */
const MAX_BUBBLES = 4;

/**
 * Fleet separation is approximate by nature, so use stable, glanceable buckets
 * instead of letting normal GPS noise flicker through the general nav formatter.
 */
export function formatFleetDistance(metres: number, units: UnitSystem): string {
  if (!Number.isFinite(metres) || metres < 0) metres = 0;
  if (units === 'imperial') {
    const feet = metres * 3.28084;
    if (feet < 1000) return `${Math.round(feet / 100) * 100} ft`;
    const miles = metres / 1609.344;
    if (miles < 1) return `${(Math.round(miles * 4) / 4).toFixed(2)} mi`;
    return miles < 10 ? `${(Math.round(miles * 2) / 2).toFixed(1)} mi` : `${Math.round(miles)} mi`;
  }
  if (metres < 100) return `${Math.round(metres / 25) * 25} m`;
  if (metres < 1000) return `${Math.round(metres / 50) * 50} m`;
  const km = metres / 1000;
  return km < 10 ? `${(Math.round(km * 5) / 5).toFixed(1)} km` : `${Math.round(km)} km`;
}

export class FleetBubble {
  readonly el: HTMLElement;
  private sheet: HTMLElement;
  private prompt: HTMLElement;
  private replyCard: HTMLElement;
  private contacts: FleetContact[] = [];
  private units: UnitSystem = 'metric';
  private selected: number | null = null;
  private promptQueue: QuickMessage[] = [];
  private promptTimer: number | null = null;
  private replyQueue: QuickMessage[] = [];
  /** What the bubble column currently shows, so identical state is not redrawn. */
  private lastBubbleKey = '';
  /** Latest answer each driver gave us, so a missed toast stays recoverable. */
  private answers = new Map<number, QuickMessage>();

  constructor(
    parent: HTMLElement,
    private actions: FleetBubbleActions,
  ) {
    // A COLUMN of bubbles, one per driver - not one bubble for the nearest with
    // the others hidden behind a count. The column is the positioned element
    // and lays its children out with a gap, so bubbles cannot overlap each
    // other however many drivers are out or however wide their names run.
    this.el = document.createElement('div');
    this.el.className = 'fleet-bubbles';
    this.el.hidden = true;
    parent.appendChild(this.el);
    // Delegated: the bubbles themselves are replaced on every roster refresh.
    this.el.addEventListener('click', (ev) => {
      const hit = (ev.target as HTMLElement).closest<HTMLElement>('[data-driver],[data-more]');
      if (!hit) return;
      this.openSheet(hit.dataset.driver ? Number(hit.dataset.driver) : null);
    });

    this.sheet = document.createElement('div');
    this.sheet.className = 'fleet-sheet';
    this.sheet.hidden = true;
    parent.appendChild(this.sheet);

    this.prompt = document.createElement('div');
    this.prompt.className = 'msg-prompt';
    this.prompt.hidden = true;
    parent.appendChild(this.prompt);

    this.replyCard = document.createElement('div');
    this.replyCard.className = 'reply-card';
    this.replyCard.hidden = true;
    this.replyCard.setAttribute('role', 'alert');
    parent.appendChild(this.replyCard);
  }

  setUnits(units: UnitSystem): void {
    this.units = units;
  }

  /** Refresh the roster. Hides the column entirely when nobody else is out. */
  update(contacts: FleetContact[]): void {
    this.contacts = contacts;
    if (!contacts.length) {
      this.el.hidden = true;
      if (this.lastBubbleKey !== '') {
        this.lastBubbleKey = '';
        this.el.innerHTML = '';
      }
      if (!this.sheet.hidden) this.renderSheet();
      return;
    }

    this.el.hidden = false;
    const shown = contacts.slice(0, MAX_BUBBLES);
    const extra = contacts.length - shown.length;

    /*
     * Rebuild only when something a driver could actually SEE has changed.
     *
     * This runs on every GPS fix, not every roster poll - the bearings are
     * recomputed against our own heading each time a fix lands. Rewriting
     * innerHTML threw away and rebuilt every bubble once a second, and these
     * bubbles carry a backdrop blur, so each rebuild forced the blurred region
     * behind them to be composited again. Measured with the roster empty it was
     * 16 writes in 24 seconds, every one of them byte-identical to what was
     * already on screen.
     *
     * The comparison is against the rendered markup rather than the raw
     * contacts: the distance in metres changes on every single fix while
     * moving, but "1.1 km" on screen does not, and it is the pixels that cost
     * something to redraw.
     */
    const html =
      shown.map((c) => this.bubbleHtml(c)).join('') +
      (extra > 0
        ? `<button class="fleet-bubble fleet-bubble--more" data-more="1">
             <span class="fleet-bubble__count">+${extra}</span>
           </button>`
        : '');
    if (html !== this.lastBubbleKey) {
      this.lastBubbleKey = html;
      this.el.innerHTML = html;
    }

    if (!this.sheet.hidden) this.renderSheet();
  }

  /** One driver's bubble: which way they are, how far, how fast, who. */
  private bubbleHtml(c: FleetContact): string {
    return `
      <button class="fleet-bubble ${c.stale ? 'is-stale' : ''}" data-driver="${c.id}"
              aria-label="${escapeHtml(c.name)}">
        <span class="fleet-bubble__arrow">
          <svg viewBox="0 0 24 24" aria-hidden="true" style="transform: rotate(${Math.round(c.relativeBearing)}deg)"><path d="M12 3 L20 20 L12 15.5 L4 20 Z" fill="currentColor"/></svg>
        </span>
        <span class="fleet-bubble__text">
          <strong>${formatFleetDistance(c.distance, this.units)}${this.speedTag(c)}</strong>
          <em>${escapeHtml(c.name)}</em>
        </span>
      </button>`;
  }

  /**
   * Record an answer so it can be read back later.
   *
   * The toast that announces a reply is gone in seconds, and a driver watching
   * the road is exactly the person who misses it - "the yes/no never came back"
   * is indistinguishable from "it came back while I was looking at a junction".
   * Keeping it on the driver's own row means the answer can always be checked.
   */
  noteReply(msg: QuickMessage): void {
    this.answers.set(msg.to, msg);
    if (!this.sheet.hidden) this.renderSheet();
  }

  /**
   * Announce an answer that has just come back, and KEEP it on screen until the
   * driver acknowledges it.
   *
   * A toast was the wrong shape for this. The whole point of asking "fuel
   * stop?" is that you act on the reply, and a three-second flash is exactly
   * what a driver watching the road misses - which read, from the cab, as the
   * answer never arriving at all. So this one waits: white, high-contrast, and
   * dismissed only by a deliberate tap on OK.
   */
  showReply(msg: QuickMessage): void {
    this.noteReply(msg);
    this.replyQueue.push(msg);
    if (this.replyCard.hidden) this.nextReply();
  }

  private nextReply(): void {
    const msg = this.replyQueue.shift();
    if (!msg) {
      this.replyCard.hidden = true;
      this.replyCard.innerHTML = '';
      return;
    }

    // `msg.to` is the driver we asked, so THEY are the one who answered;
    // `fromName` on the wire is us. Fall back to the stored name if they have
    // since dropped off the roster.
    const who = this.contacts.find((c) => c.id === msg.to)?.name ?? '';
    const yes = msg.reply === 'yes';

    this.replyCard.hidden = false;
    this.replyCard.className = `reply-card reply-card--${yes ? 'yes' : 'no'}`;
    this.replyCard.innerHTML = `
      <div class="reply-card__who">${escapeHtml(who)}</div>
      <div class="reply-card__asked">
        <span class="reply-card__kind">${ICONS[msg.kind] ?? ''}</span>
        ${escapeHtml(t(`msg.${msg.kind}`))}
      </div>
      <div class="reply-card__answer">${escapeHtml(t(yes ? 'msg.yes' : 'msg.no'))}</div>
      <button class="btn btn--big reply-card__ok" data-act="ok">${escapeHtml(t('msg.ok'))}</button>
      ${this.replyQueue.length ? `<div class="reply-card__more">+${this.replyQueue.length}</div>` : ''}`;

    this.replyCard
      .querySelector('[data-act=ok]')
      ?.addEventListener('click', () => this.nextReply());
  }

  /**
   * How fast they are going, beside the distance. Answers the question the
   * distance alone cannot: a car 2 km ahead at 90 km/h is pulling away, the
   * same car stopped is waiting for you - or broken down.
   *
   * Omitted rather than shown as "--" when there is no usable figure: a stale
   * report means the number is old, and a phone that never reported speed
   * would otherwise claim a standstill it cannot know about.
   */
  private speedTag(c: FleetContact): string {
    if (c.stale || c.speed == null || !Number.isFinite(c.speed)) return '';
    return `<small>${formatSpeed(c.speed, this.units)} ${speedUnitLabel(this.units)}</small>`;
  }

  /* ----------------------------- roster sheet ---------------------------- */

  /** Opens with the tapped driver already selected, so one tap starts the ask. */
  private openSheet(preselect: number | null = null): void {
    this.selected = preselect ?? this.contacts[0]?.id ?? null;
    this.sheet.hidden = false;
    this.renderSheet();
  }

  /** Open the roster from a car marker with that driver already selected. */
  openDriver(id: number): void {
    this.openSheet(id);
  }

  closeSheet(): void {
    this.sheet.hidden = true;
  }

  private renderSheet(): void {
    if (!this.contacts.length) {
      this.sheet.innerHTML = `
        <div class="sheet__grab"></div>
        <div class="fleet-sheet__head"><h3>${t('fleet.title')}</h3>
          <button class="icon-btn" data-act="close" aria-label="${t('preview.cancel')}">${X_SVG}</button></div>
        <div class="panel__hint">${t('fleet.nobody')}</div>`;
      this.sheet.querySelector('[data-act=close]')?.addEventListener('click', () => this.closeSheet());
      return;
    }

    const rows = this.contacts
      .map(
        (c) => `
        <button class="fleet-row ${c.id === this.selected ? 'is-selected' : ''} ${c.stale ? 'is-stale' : ''}"
                data-driver="${c.id}">
          <span class="fleet-row__arrow" style="transform: rotate(${Math.round(this.relative(c))}deg)">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3 L20 20 L12 15.5 L4 20 Z" fill="currentColor"/></svg>
          </span>
          <span class="fleet-row__name">${escapeHtml(c.name)}
            ${this.answerLine(c.id) || (c.destination ? `<em>${escapeHtml(c.destination)}</em>` : '')}
          </span>
          <span class="fleet-row__dist">${formatFleetDistance(c.distance, this.units)}
            ${c.stale ? `<em>${t('fleet.stale')}</em>` : ''}
          </span>
        </button>`,
      )
      .join('');

    const target = this.contacts.find((c) => c.id === this.selected) ?? this.contacts[0];

    this.sheet.innerHTML = `
      <div class="sheet__grab"></div>
      <div class="fleet-sheet__head">
        <h3>${t('fleet.title')}</h3>
        <button class="icon-btn" data-act="close" aria-label="${t('preview.cancel')}">${X_SVG}</button>
      </div>
      <div class="fleet-rows">${rows}</div>
      <div class="fleet-sheet__ask">${t('fleet.ask', { name: escapeHtml(target.name) })}</div>
      <div class="quick-msgs">
        ${KINDS.map(
          ({ kind, icon }) => `
          <button class="quick-msg quick-msg--${kind}" data-kind="${kind}">
            <span class="quick-msg__icon">${icon}</span>
            <span>${t(`msg.${kind}`)}</span>
          </button>`,
        ).join('')}
      </div>
      ${
        this.actions.shareableLocation()
          ? `<button class="btn btn--big fleet-sheet__share fleet-sheet__share--location" data-act="share-location">
               <span>📍</span>
               <span>${t('fleet.sendLocation', { name: escapeHtml(target.name) })}</span>
             </button>`
          : ''
      }
      ${
        this.actions.shareableDest()
          ? `<button class="btn btn--big fleet-sheet__share" data-act="share-dest">
               <span>📍</span>
               <span>${t('fleet.sendDest', { name: escapeHtml(target.name) })}</span>
               <em>${escapeHtml(this.actions.shareableDest()!.name)}</em>
             </button>`
          : ''
      }`;

    this.sheet.querySelector('[data-act=close]')?.addEventListener('click', () => this.closeSheet());
    this.sheet.querySelectorAll<HTMLButtonElement>('[data-driver]').forEach((b) =>
      b.addEventListener('click', () => {
        this.selected = Number(b.dataset.driver);
        this.renderSheet();
      }),
    );
    this.sheet.querySelectorAll<HTMLButtonElement>('[data-kind]').forEach((b) =>
      b.addEventListener('click', () => {
        const to = this.selected;
        if (to == null) return;
        this.actions.onSend(to, b.dataset.kind as MessageKind);
        this.closeSheet();
      }),
    );
    this.sheet.querySelector('[data-act=share-dest]')?.addEventListener('click', () => {
      const to = this.selected;
      if (to == null) return;
      this.actions.onShareDest(to);
      this.closeSheet();
    });
    this.sheet.querySelector('[data-act=share-location]')?.addEventListener('click', () => {
      const to = this.selected;
      if (to == null) return;
      this.actions.onShareLocation(to);
      this.closeSheet();
    });
  }

  private relative(c: FleetContact): number {
    return c.relativeBearing;
  }

  /**
   * "Fuel stop? - Yes" under a driver's name, rendered from the stored enums so
   * it is in THIS device's language, exactly like the toast that preceded it.
   * Drops away after an hour: an answer that old is history, not an answer.
   */
  private answerLine(driverId: number): string {
    const a = this.answers.get(driverId);
    if (!a || !a.reply) return '';
    if (Date.now() - (a.repliedAt ?? a.at) > 3_600_000) return '';
    const asked = t(`msg.${a.kind}`);
    const said = t(a.reply === 'yes' ? 'msg.yes' : 'msg.no');
    return `<em class="fleet-row__answer fleet-row__answer--${a.reply}">${escapeHtml(asked)} ${escapeHtml(said)}</em>`;
  }

  /* --------------------------- incoming prompt --------------------------- */

  /** Show a yes/no prompt. Queues if one is already up. */
  showIncoming(msg: QuickMessage): void {
    this.promptQueue.push(msg);
    if (this.prompt.hidden) this.nextPrompt();
  }

  private nextPrompt(): void {
    const msg = this.promptQueue.shift();
    if (!msg) {
      this.prompt.hidden = true;
      return;
    }

    this.prompt.hidden = false;
    this.prompt.className = `msg-prompt msg-prompt--${msg.kind}`;
    /*
     * A shared destination is a different question from food/fuel/help: the
     * yes button IS the action (start navigating there), so it is labelled
     * with what it does. The reply still travels as yes/no, which shows the
     * sender "Destination - Yes" through the ordinary reply card.
     */
    const isDest = msg.kind === 'dest' && !!msg.dest;
    this.prompt.innerHTML = `
      <div class="msg-prompt__from">${escapeHtml(msg.fromName)}</div>
      <div class="msg-prompt__body">
        <span class="msg-prompt__icon">${ICONS[msg.kind] ?? ''}</span>
        <span>${t(`msg.${msg.kind}`)}${isDest ? `<em class="msg-prompt__dest">${escapeHtml(msg.dest!.name)}</em>` : ''}</span>
      </div>
      <div class="msg-prompt__actions">
        <button class="btn btn--big msg-prompt__no" data-reply="no">${t(isDest ? 'msg.dest.dismiss' : 'msg.no')}</button>
        <button class="btn btn--big btn--primary msg-prompt__yes" data-reply="yes">${t(isDest ? 'msg.dest.goto' : 'msg.yes')}</button>
      </div>`;

    const answer = (reply: MessageReply): void => {
      if (this.promptTimer !== null) clearTimeout(this.promptTimer);
      this.actions.onReply(msg.id, reply);
      if (isDest && reply === 'yes') this.actions.onGotoDest(msg.dest!);
      this.nextPrompt();
    };
    this.prompt
      .querySelectorAll<HTMLButtonElement>('[data-reply]')
      .forEach((b) => b.addEventListener('click', () => answer(b.dataset.reply as MessageReply)));

    // Don't leave a question covering the map forever if nobody answers.
    if (this.promptTimer !== null) clearTimeout(this.promptTimer);
    this.promptTimer = window.setTimeout(() => this.nextPrompt(), 90_000);
  }
}

const X_SVG = `<svg viewBox="0 0 24 24"><path d="M6 6 L18 18 M18 6 L6 18" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>`;

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}
